import { TFunction } from 'next-i18next';

import React from 'react';

import { atomic } from '@thinkific/chalk/atomic';
import { Button, LoadingSpinner } from '@thinkific/chalk/react-components';
import { X } from '@thinkific/chalk/react-icons';

import { Attachment } from 'schema/Post/types';

import styles from './ImagePreview.module.scss';

interface ImagePreviewProps {
  attachment: Attachment;
  onRemove: () => void;
  openAltTextModal: () => void;
  t: TFunction; // Would be better to pass in the translated strings used here
}

const imagePreviewContainer = atomic({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  boxSizing: 'border-box',
});

const buttonBase = atomic({
  position: 'absolute',
});

const Image = ({ fileName, url, onRemove, openAltTextModal, isLoading, t }) => {
  return (
    <div
      aria-live="polite"
      className={styles['image-preview']}
      data-qa="image-preview"
      id="image-preview"
      role="region"
      title={t('Image Preview')}
    >
      <img alt={`Preview of ${fileName}`} className={styles['image-preview__image']} src={url} />
      {isLoading ? (
        <div className={styles['loading-spinner-button']}>
          <LoadingSpinner data-qa="image-preview__loading-spinner" size="sm" onDark />
        </div>
      ) : (
        <>
          <Button
            className={`${buttonBase} ${styles['alt-image-button']}`}
            data-qa="image-preview__add-alt-text-button"
            emphasis="low"
            size="xs"
            onClick={openAltTextModal}
            onDark
          >
            {t('components-imagePreview-plus-alt', '+ Alt')}
          </Button>
          <Button
            className={`${buttonBase} ${styles['remove-image-button']}`}
            data-qa="image-preview__remove-button"
            icon={X}
            size="xs"
            hiddenLabel
            onClick={onRemove}
          >
            {t('Remove', 'Remove')}
          </Button>
        </>
      )}
    </div>
  );
};

const ImagePreview = ({ attachment, onRemove, openAltTextModal, t }: ImagePreviewProps) => {
  const { fileName, url, isLoading } = attachment;
  return (
    <div className={imagePreviewContainer}>
      <Image
        fileName={fileName}
        isLoading={isLoading}
        openAltTextModal={() => openAltTextModal()}
        t={t}
        url={url}
        onRemove={onRemove}
      />
    </div>
  );
};

export default ImagePreview;
